import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useFingerprint } from './useFingerprint';
import api from '@/services/api';

// Shared state across all composable instances
const featureAccess = ref(null);
const loading = ref(false);
const error = ref(null);
const lastFetched = ref(null);

// Cache duration: 1 minute
const CACHE_DURATION_MS = 60 * 1000;

export function useFeatureAccess() {
    const authStore = useAuthStore();
    const { getFingerprint, fingerprint } = useFingerprint();

    // Computed properties based on feature access
    const canUseCanvas = computed(() => {
        return true;  // Canvas available for everyone
    });

    const canUseFilter = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return true;
        return featureAccess.value?.can_use_filter ?? false;
    });

    const canUseHints = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return true;
        return featureAccess.value?.can_use_hints ?? false;
    });

    const canSubmit = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return true;
        return featureAccess.value?.can_submit ?? true;
    });

    const questionsRemaining = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 999;
        return featureAccess.value?.questions_remaining ?? 0;
    });

    const questionsLimit = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 999;
        return featureAccess.value?.questions_limit ?? 3;
    });

    const userType = computed(() => {
        if (authStore.hasTierAccess('TIER 1')) return 'premium';
        if (authStore.isAuthenticated) return 'free';
        return featureAccess.value?.user_type ?? 'guest';
    });

    const resetsAt = computed(() => {
        return featureAccess.value?.resets_at ?? null;
    });

    const isPremium = computed(() => userType.value === 'premium');
    const isFreeUser = computed(() => userType.value === 'free');
    const isGuest = computed(() => userType.value === 'guest');
    const isPromoMode = computed(() => featureAccess.value?.is_promo_mode ?? false);

    /**
     * Fetch feature access from the API
     */
    async function fetchFeatureAccess(forceRefresh = false) {
        // Skip if premium user
        if (authStore.hasTierAccess('TIER 1')) {
            featureAccess.value = {
                can_use_canvas: true,
                can_use_filter: true,
                can_use_hints: true,
                can_submit: true,
                questions_remaining: 999,
                questions_limit: 999,
                user_type: 'premium',
                resets_at: null,
                is_promo_mode: false
            };
            return featureAccess.value;
        }

        // Check cache validity
        if (!forceRefresh && lastFetched.value) {
            const elapsed = Date.now() - lastFetched.value;
            if (elapsed < CACHE_DURATION_MS && featureAccess.value) {
                return featureAccess.value;
            }
        }

        loading.value = true;
        error.value = null;

        try {
            const fp = await getFingerprint();
            const response = await api.checkFeatureAccess({
                fingerprint: fp,
                user_id: authStore.user?.id ?? null
            });

            featureAccess.value = response.data;
            lastFetched.value = Date.now();
            return response.data;
        } catch (err) {
            console.error('Failed to fetch feature access:', err);
            error.value = err;

            // Fallback to restrictive defaults on error
            if (!featureAccess.value) {
                featureAccess.value = {
                    can_use_canvas: true,  // Canvas available for everyone
                    can_use_filter: false,
                    can_use_hints: false,
                    can_submit: false,
                    questions_remaining: 0,
                    questions_limit: authStore.isAuthenticated ? 5 : 3,
                    user_type: authStore.isAuthenticated ? 'free' : 'guest',
                    resets_at: null,
                    is_promo_mode: false
                };
            }
            return featureAccess.value;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Update local state after a question is submitted
     */
    function decrementRemaining() {
        if (featureAccess.value && featureAccess.value.questions_remaining > 0) {
            featureAccess.value = {
                ...featureAccess.value,
                questions_remaining: featureAccess.value.questions_remaining - 1,
                can_submit: featureAccess.value.questions_remaining - 1 > 0
            };
        }
    }

    /**
     * Update from submit response
     */
    function updateFromSubmitResponse(response) {
        if (response.questions_remaining !== undefined && response.questions_remaining !== null) {
            if (featureAccess.value) {
                featureAccess.value = {
                    ...featureAccess.value,
                    questions_remaining: response.questions_remaining,
                    can_submit: response.questions_remaining > 0
                };
            }
        }
    }

    /**
     * Clear cached data (useful on logout)
     */
    function clearCache() {
        featureAccess.value = null;
        lastFetched.value = null;
    }

    // Watch for auth changes and refresh
    watch(() => authStore.isAuthenticated, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            clearCache();
            fetchFeatureAccess(true);
        }
    });

    return {
        // State
        featureAccess,
        loading,
        error,

        // Computed
        canUseCanvas,
        canUseFilter,
        canUseHints,
        canSubmit,
        questionsRemaining,
        questionsLimit,
        userType,
        resetsAt,
        isPremium,
        isFreeUser,
        isGuest,
        isPromoMode,

        // Methods
        fetchFeatureAccess,
        decrementRemaining,
        updateFromSubmitResponse,
        clearCache
    };
}
